/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { ISubscriptionPlan } from "@/interfaces/SubscriptionPlans";
import { api } from "../lib/api";
import { IApiExtendedParams } from "@/interfaces/Common.interface";
import { ICheckoutSession } from "@/interfaces/Checkout.interface";

/**
 * Subscription Plans
 *
 * @returns A Promise that resolves to an array of strings
 */
export const getSubscriptionPlans = (): Promise<ISubscriptionPlan[]> => {
  return api.get(`/subscription-plans`, {
    withAuth: true,
  });
};

/**
 * Cancel Subscription
 *
 * @returns A Promise
 */
export const cancelSubscription = ({ workspaceId, projectId }: IApiExtendedParams): Promise<any> => {
  return api.delete(`/workspaces/${workspaceId}/projects/${projectId}/subscriptions`, {
    withAuth: true,
  });
};

/**
 * Set a Subscription
 *
 * @returns A Promise that resolves to an array of strings
 */
export const setSubscription = ({
  workspaceId,
  projectId,
  currency = "eur",
  priceId,
}: {
  workspaceId: string;
  projectId: string;
  currency: string;
  priceId: string;
}): Promise<any> => {
  const payload: Record<string, any> = {
    currency,
    price_id: priceId,
  };
  return api.post(
    `/workspaces/${workspaceId}/projects/${projectId}/subscriptions`,
    {
      ...payload,
    },
    {
      withAuth: true,
    }
  );
};

/**
 * Update a subscription with a new price id
 * @param workspaceId
 * @param projectId
 * @param priceId
 * @returns A promise that resolves to a string
 */
export const updateSubscription = ({
  workspaceId,
  projectId,
  priceId,
}: {
  workspaceId: string;
  projectId: string;
  priceId: string;
}): Promise<string> => {
  return api.patch(`/workspaces/${workspaceId}/projects/${projectId}/subscriptions`, { price_id: priceId }, { withAuth: true });
};

/**
 * User Checkout Session Status
 *
 * @returns A Promise that resolves to an object of the checkout session status
 */
export const getCheckoutStatus = (userId: string, checkoutSessionId: string): Promise<ICheckoutSession> => {
  return api.get(`/users/${userId}/checkout-sessions/${checkoutSessionId}`, {
    withAuth: true,
  });
};
